'use client';

import React, { useState } from 'react';
import { FaEnvelope } from 'react-icons/fa';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const simulateSubscribe = async () => {
    setStatus('loading');
    setMessage(null);

    await new Promise((r) => setTimeout(r, 1500));

    const ok = Math.random() < 0.8;
    if (ok) {
      setStatus('success');
      setMessage('You’re in! We’ll keep you posted.');
      setEmail('');
    } else {
      setStatus('error');
      setMessage('Oops, something went wrong. Please try again.');
    }

    setTimeout(() => {
      setStatus('idle');
      setMessage(null);
    }, 3000);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || status === 'loading') return;
    simulateSubscribe();
  };

  const isLoading = status === 'loading';

  return (
    <div className="w-full py-8 sm:py-10">
      <div className="w-full h-[1px] sm:h-[1px] bg-[#f9f9f9] mt-2" />

      <section className="w-full">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl leading-tight font-bold text-black tracking-tight text-center">
            Stay in the know
          </h1>

          <div className="mt-2 sm:mt-3 text-center text-sm sm:text-base italic font-semibold leading-6 text-[#2f2f2f] max-w-2xl">
            <p>
              We&apos;re keeping you in the know on the latest features that
              matter to you.
            </p>
            <p>
              By entering your email below, you are subscribed to the updates.
            </p>
          </div>

          <div
            className={`mt-3 h-0 transition-all duration-300 ease-out overflow-hidden ${
              message ? 'h-10' : 'h-0'
            }`}
            aria-live="polite"
          >
            {message && (
              <div
                className={`mx-auto w-full max-w-md sm:max-w-lg md:max-w-xl text-center text-sm font-medium rounded-md px-3 py-2 ${
                  status === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {message}
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-4 sm:mt-6 w-full flex justify-center"
          >
            <div className="w-full max-w-md sm:max-w-lg md:max-w-xl">
              <label htmlFor="newsletter-email" className="sr-only">
                Email address
              </label>

              <div className="flex items-stretch rounded-lg ring-1 ring-[#0f0f0f] overflow-hidden focus-within:ring-2 focus-within:ring-[var(--color-primary)] bg-white">
                <div
                  className="flex items-center justify-center px-3"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  <FaEnvelope className="text-white text-lg sm:text-xl" />
                </div>

                <input
                  id="newsletter-email"
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 h-10 sm:h-12 px-3 sm:px-4 placeholder-[#6b6b6b] text-[#1f1f1f] text-sm sm:text-base focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                  aria-label="Email address"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`hidden sm:inline-flex items-center gap-2 px-4 text-sm font-medium text-white disabled:opacity-70 disabled:cursor-not-allowed`}
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  {isLoading && (
                    <svg
                      className="animate-spin h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        opacity="0.25"
                      />
                      <path
                        d="M22 12a10 10 0 00-10-10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                    </svg>
                  )}
                  {isLoading ? 'Submitting…' : 'Subscribe'}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="sm:hidden mt-3 w-full h-10 rounded-lg text-sm font-medium text-white disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {isLoading && (
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      opacity="0.25"
                    />
                    <path
                      d="M22 12a10 10 0 00-10-10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                  </svg>
                )}
                {isLoading ? 'Submitting…' : 'Subscribe'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
